import React, { useState, useEffect, useMemo } from "react";
import { Route, Link, Redirect, Switch } from "react-router-dom";
import { PhGridPagination, PhGridFilter, PhGridSort } from 'components/PhGrid';
import { API } from "aws-amplify";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { InlineSpinner } from "components/Spinner";
import { sortAlphabetical, findArrayIndex } from "util/dashboardManagement";

function formatDate(expirationDate) {
    return `${expirationDate.getFullYear()}-${(expirationDate.getMonth() + 1) < 10 ? '0' + (expirationDate.getMonth() + 1) : (expirationDate.getMonth() + 1)}-${expirationDate.getDate().toString().padStart(2, '0')}`
}

function isDashboardQueryVis(id) {
    return id.includes('dash_query_vis_');
}

function DashboardsContainer() {

    const getActiveTabFromPath = () => {
        if (typeof window === 'undefined') return 'owned';
        const pathSegments = window.location.pathname.split('/');
        const tabFromUrl = pathSegments[2];
        return ['owned', 'shared'].includes(tabFromUrl) ? tabFromUrl : 'owned';
    };

    const [ownedPaginationOffset, setOwnedPaginationOffset] = useState(0);
    const [ownedPaginationSize, setOwnedPaginationSize] = useState(10);
    const [sharedPaginationOffset, setSharedPaginationOffset] = useState(0);
    const [sharedPaginationSize, setSharedPaginationSize] = useState(10);
    const [filterString, setFilterString] = useState('');
    const [sortColumn, setSortColumn] = useState('');
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState('ascending');
    const [profile, setProfile] = useState(null);
    const [dashboardUser, setDashboardUser] = useState(null);
    const [activeTab, setActiveTab] = useState(getActiveTabFromPath());
    const [dashboards, setDashboards] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedDashboard, setSelectedDashboard] = useState(null);
    const [waitingForDashboards, setWaitingForDashboards] = useState(false);
    const [pinning, setPinning] = useState(null);
    const [renewing, setRenewing] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [waitingForDashboard, setWaitingForDashboard] = useState(null);
    const [availableBrands, setAvailableBrands] = useState([]);
    const [availableAccounts, setAvailableAccounts] = useState([]);
    const [sharedUsers, setSharedUsers] = useState([]);
    const [sharedAccounts, setSharedAccounts] = useState();
    const [sharedBrands, setSharedBrands] = useState();
    const [showAddModal, setShowAddModal] = useState(false);

    async function getAllDashboards(renewedDashboardId = '') {
        try {
            const dashboardsPage1 = await API.get('DashboardApi', '/dashboards');

            setDashboards(dashboardsPage1.dashboards.map(dashboard => {
                if (dashboard.owner) {
                    dashboard['ownership'] = 'shared';
                } else {
                    dashboard['ownership'] = 'owned';
                }
                if (dashboard.id === renewedDashboardId) {
                    dashboard['renewed'] = true;
                }
                return dashboard;
            }));

            setWaitingForDashboards(false);
            setRenewing(null);
            setPinning(null);

            if (dashboardsPage1.pageCount > 1) {
                toast(`First 100 dashboards loaded. Retrieving remaining dashboards in the background`, { type: toast.TYPE.INFO, autoClose: 5000 });
                let dashboardPages = [...dashboardsPage1.dashboards];

                // pages are 0 indexed
                for (let page = 1; page < dashboardsPage1.pageCount; page++) {
                    const nextPage = await API.get('DashboardApi', `/dashboards?page=${page}`);
                    dashboardPages = dashboardPages.concat(nextPage.dashboards);
                }

                dashboardPages.map(dashboard => {
                    if (dashboard.owner) {
                        dashboard['ownership'] = 'shared';
                    } else {
                        dashboard['ownership'] = 'owned';
                    }
                    if (dashboard.id === renewedDashboardId) {
                        dashboard['renewed'] = true;
                    }
                    return dashboard;
                });

                setDashboards(dashboardPages);
                toast(`All dashboards loaded successfully`, { type: toast.TYPE.SUCCESS, autoClose: 5000 });
            }
        } catch (err) {
            toast(`There was a problem loading your dashboards`, { type: toast.TYPE.ERROR, autoClose: 5000 });
            setWaitingForDashboards(false);
            setRenewing(null);
            setPinning(null);
        }
    }

    useEffect(() => {
        async function init() {
            setWaitingForDashboards(true);
            try {
                const initialRequests = await Promise.allSettled([
                    API.get('AccountGatewayApi', '/profile', { withCredentials: false }),
                    API.get('DashboardApi', '/user')
                ]);

                if (initialRequests[0].status === 'fulfilled') {
                    setProfile(initialRequests[0].value);
                }
                let dashboardUserPermissions = {
                    "accountAuthorship": [],
                    "brandAuthorship": [],
                    "permissions": 'reader'
                };
                if (initialRequests[1].status === 'fulfilled') {
                    dashboardUserPermissions = initialRequests[1].value;
                    setDashboardUser(dashboardUserPermissions);
                }
                getAllDashboards();

                const userBrands = dashboardUserPermissions.brandAuthorship || [];
                let userAccounts = dashboardUserPermissions.accountAuthorship || [];

                const brandsWithAccounts = [];
                await Promise.allSettled(userBrands?.map(async brandName => {
                    const brandInfo = await API.get('AccountGatewayApi', `/brand/${brandName}`, { withCredentials: true });
                    brandInfo.accounts = sortAlphabetical(brandInfo?.accounts || [], 'name');
                    userAccounts = userAccounts.concat(brandInfo.accounts);
                    return brandInfo;
                })).then(brandPromises => {
                    brandPromises.forEach(brandPromise => {
                        if (brandPromise.status === 'fulfilled') {
                            brandsWithAccounts.push(brandPromise.value);
                        }
                    })
                });

                const userAccountsDeduped = dashboardUserPermissions.permissions === 'author' ? [] : initialRequests[0].value?.accounts;
                userAccounts?.forEach(account => {
                    if (account.uuid) {
                        userAccountsDeduped.push(account);
                    } else if (account.id) {
                        const index = userAccounts.findIndex(item => item.uuid === account.id);
                        if (index === -1) {
                            userAccountsDeduped.push(account);
                        }
                    }
                });

                await Promise.allSettled(userAccountsDeduped.map(async account => {
                    const accountId = account.id || account.uuid;
                    const accountInfo = await API.get('AccountGatewayApi', `/account/${accountId}`, { withCredentials: true });
                    account['users'] = sortAlphabetical(accountInfo?.profiles || [], 'email');
                    return account;
                }));

                if (brandsWithAccounts.length === 0) {
                    setAvailableBrands(['none']);
                } else {
                    setAvailableBrands([...sortAlphabetical(brandsWithAccounts || [], 'name')]);
                }
                if (userAccountsDeduped.length === 0) {
                    setAvailableAccounts(['none']);
                } else {
                    setAvailableAccounts([...sortAlphabetical(userAccountsDeduped || [], 'name')]);
                }
            } catch (err) {
                console.error(err);
                toast(`There was a problem loading data in order to share dashboards`, { type: toast.TYPE.ERROR, autoClose: 5000 });
                setWaitingForDashboards(false);
            }
        }
        init();
    }, []);

    function calcLifetime(expiration) {
        const expirationDate = new Date(expiration)
        const today = new Date();
        const monthsRemaining = expirationDate.getMonth() - today.getMonth() + (12 * (expirationDate.getFullYear() - today.getFullYear()));
        if (monthsRemaining > 1) {
            return `${monthsRemaining} months remaining`
        }
        const daysRemaining = Math.round((expirationDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
        return `${daysRemaining} days remaining`
    }

    const createDisplayList = useMemo(() => (ownership, offset, size) => {
        if (!dashboards) return [];
        return dashboards
            .filter(dashboard => dashboard.ownership === ownership)
            .filter(dashboard => [
                dashboard.id,
                dashboard.name || '',
                dashboard.description || 'No Description',
                calcLifetime(dashboard.expiration) || ''
            ].join('').toLowerCase().includes(filterString.toLowerCase()))
            .sort((a, b) => {
                if (sortColumn) {
                    if (sortDirection === 'ascending') {
                        return ((a.pinned - b.pinned) || (sortKey(a) > sortKey(b))) ? 1 : ((a.pinned - b.pinned) || (sortKey(a) < sortKey(b))) ? -1 : 0;
                    } else {
                        return ((a.pinned - b.pinned) || (sortKey(a) < sortKey(b))) ? 1 : ((a.pinned - b.pinned) || (sortKey(a) > sortKey(b))) ? -1 : 0;
                    }
                } else {
                    return;
                }
            })
            .slice(offset, offset + size);
    }, [dashboards, filterString, sortColumn, sortKey, sortDirection]);

    const ownedDisplayList = createDisplayList('owned', ownedPaginationOffset, ownedPaginationSize);
    const sharedDisplayList = createDisplayList('shared', sharedPaginationOffset, sharedPaginationSize);

    function displayDashboards(dashboard) {
        return (
            <tr key={dashboard.id}>
                {dashboard.pinned ? <td style={{ textAlign: 'center' }}>
                    <OverlayTrigger
                        key="dashboardUnpin"
                        placement="top"
                        overlay={<Tooltip>Unpin this dashboard to the remove from the top of your page</Tooltip>}
                    >
                        <button type="button" title="Pinned" className='btn btn-primary font-weight-bold m-1 px-2 py-1' onClick={() => updatePin(dashboard.id, true)}>
                            {pinning === dashboard.id ? <InlineSpinner /> : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="20" height="15" fill="#FFF" style={{ rotate: '45deg' }}>
                                <path d="M32 32C32 14.3 46.3 0 64 0L320 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-29.5 0 11.4 148.2c36.7 19.9 65.7 53.2 79.5 94.7l1 3c3.3 9.8 1.6 20.5-4.4 28.8s-15.7 13.3-26 13.3L32 352c-10.3 0-19.9-4.9-26-13.3s-7.7-19.1-4.4-28.8l1-3c13.8-41.5 42.8-74.8 79.5-94.7L93.5 64 64 64C46.3 64 32 49.7 32 32zM160 384l64 0 0 96c0 17.7-14.3 32-32 32s-32-14.3-32-32l0-96z" />
                            </svg>}
                        </button>
                    </OverlayTrigger>
                </td> : <td style={{ textAlign: 'center' }}>
                    <OverlayTrigger
                        key="dashboardPin"
                        placement="top"
                        overlay={<Tooltip>Pin this dashboard to the top of your page</Tooltip>}
                    >
                        <button type="button" title="Unpinned" className='btn btn-primary font-weight-bold m-1 px-2 py-1' onClick={() => updatePin(dashboard.id, false)}>
                            {pinning === dashboard.id ? <InlineSpinner /> : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" width="20" height="15" fill="#FFF">
                                <path d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L481.4 352c9.8-.4 18.9-5.3 24.6-13.3c6-8.3 7.7-19.1 4.4-28.8l-1-3c-13.8-41.5-42.8-74.8-79.5-94.7L418.5 64 448 64c17.7 0 32-14.3 32-32s-14.3-32-32-32L192 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l29.5 0-6.1 79.5L38.8 5.1zM324.9 352L177.1 235.6c-20.9 18.9-37.2 43.3-46.5 71.3l-1 3c-3.3 9.8-1.6 20.5 4.4 28.8s15.7 13.3 26 13.3l164.9 0zM288 384l0 96c0 17.7 14.3 32 32 32s32-14.3 32-32l0-96-64 0z" />
                            </svg>}
                        </button>
                    </OverlayTrigger>
                </td>}
                <td>{dashboard.name || ''}</td>
                <td>{dashboard.description || 'No Description'}</td>
                {activeTab === 'owned' && <td>{isDashboardQueryVis(dashboard.id) ? 'N/A' : (calcLifetime(dashboard.expiration) || '')}</td>}
                <td style={{ textAlign: 'center' }}>
                    <OverlayTrigger
                        key="dashboardVisualize"
                        placement="top"
                        overlay={<Tooltip>Visualize this dashboard</Tooltip>}
                    >
                        <label className="col-form-label p-0">
                            {isDashboardQueryVis(dashboard.id) ?
                                <Link to={`/queryvisualization/${dashboard.id}`} title="Visualize" className={'btn btn-primary font-weight-bold m-1 px-2 py-1'}>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="15" height="15" fill="#FFF">
                                        <path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z" />
                                    </svg>
                                </Link> :
                                <Link to={`/dashboardVisualization/${dashboard.id}`} title="Visualize" className={'btn btn-primary font-weight-bold m-1 px-2 py-1'}>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="15" height="15" fill="#FFF">
                                        <path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z" />
                                    </svg>
                                </Link>
                            }
                        </label>
                    </OverlayTrigger>
                </td>
                {!dashboard.owner ? <td style={{ textAlign: 'center' }}>
                    <OverlayTrigger
                        key="dashboardShare"
                        placement="top"
                        overlay={(dashboard.description && dashboard.updateFrequency && (dashboardUser?.permissions === 'author' || isDashboardQueryVis(dashboard.id))) ? <Tooltip>Share this dashboard with other members in your organization</Tooltip> : (dashboardUser?.permissions !== 'author') ? <Tooltip>Only authors can share dashboards</Tooltip> : <Tooltip>Edit dashboard with required information in order to share</Tooltip>}
                    >
                        <button type="button" title="Share" className='btn btn-primary font-weight-bold m-1 px-2 py-1' disabled={(dashboard.description && dashboard.updateFrequency && (dashboardUser?.permissions === 'author' || isDashboardQueryVis(dashboard.id))) ? false : true} onClick={() => { getDashboard(dashboard) }}>
                            {waitingForDashboard === dashboard.id ? <InlineSpinner /> : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="15" height="15" fill="#FFF">
                                <path d="M307 34.8c-11.5 5.1-19 16.6-19 29.2l0 64-112 0C78.8 128 0 206.8 0 304C0 417.3 81.5 467.9 100.2 478.1c2.5 1.4 5.3 1.9 8.1 1.9c10.9 0 19.7-8.9 19.7-19.7c0-7.5-4.3-14.4-9.8-19.5C108.8 431.9 96 414.4 96 384c0-53 43-96 96-96l96 0 0 64c0 12.6 7.4 24.1 19 29.2s25 3 34.4-5.4l160-144c6.7-6.1 10.6-14.7 10.6-23.8s-3.8-17.7-10.6-23.8l-160-144c-9.4-8.5-22.9-10.6-34.4-5.4z" />
                            </svg>}
                        </button>
                    </OverlayTrigger>
                </td> : <td />}
                {!dashboard.owner ? <td style={{ textAlign: 'center' }}>
                    <OverlayTrigger
                        key="dashboardEdit"
                        placement="top"
                        overlay={<Tooltip>Edit your dashboards information</Tooltip>}
                    >
                        <button type="button" title="Edit" className='btn btn-primary font-weight-bold m-1 px-2 py-1' onClick={() => { setSelectedDashboard(dashboard), setShowEditModal(true) }}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 117.74 122.88" width="15" height="15" fill="#FFF">
                                <path
                                    d="M94.62,2c-1.46-1.36-3.14-2.09-5.02-1.99c-1.88,0-3.56,0.73-4.92,2.2L73.59,13.72l31.07,30.03l11.19-11.72 c1.36-1.36,1.88-3.14,1.88-5.02s-0.73-3.66-2.09-4.92L94.62,2L94.62,2L94.62,2z M41.44,109.58c-4.08,1.36-8.26,2.62-12.35,3.98 c-4.08,1.36-8.16,2.72-12.35,4.08c-9.73,3.14-15.07,4.92-16.22,5.23c-1.15,0.31-0.42-4.18,1.99-13.6l7.74-29.61l0.64-0.66 l30.56,30.56L41.44,109.58L41.44,109.58L41.44,109.58z M22.2,67.25l42.99-44.82l31.07,29.92L52.75,97.8L22.2,67.25L22.2,67.25z"
                                />
                            </svg>
                        </button>
                    </OverlayTrigger>
                </td> : <td />}
                {(!dashboard.owner && !isDashboardQueryVis(dashboard.id)) ? <td style={{ textAlign: 'center' }}>
                    <OverlayTrigger
                        key="dashboardRenew"
                        placement="top"
                        overlay={<Tooltip>Renew the lifetime of your dashboard</Tooltip>}
                    >
                        <button type="button" title="Renew" className='btn btn-primary font-weight-bold m-1 px-2 py-1' onClick={() => renewDashboard(dashboard.id)}>
                            {renewing === dashboard.id ? <InlineSpinner /> : dashboard.renewed ?
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="15" height="15" fill="#FFF">
                                    <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" />
                                </svg> : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="15" height="15" fill="#FFF">
                                    <path d="M142.9 142.9c-17.5 17.5-30.1 38-37.8 59.8c-5.9 16.7-24.2 25.4-40.8 19.5s-25.4-24.2-19.5-40.8C55.6 150.7 73.2 122 97.6 97.6c87.2-87.2 228.3-87.5 315.8-1L455 55c6.9-6.9 17.2-8.9 26.2-5.2s14.8 12.5 14.8 22.2l0 128c0 13.3-10.7 24-24 24l-8.4 0c0 0 0 0 0 0L344 224c-9.7 0-18.5-5.8-22.2-14.8s-1.7-19.3 5.2-26.2l41.1-41.1c-62.6-61.5-163.1-61.2-225.3 1zM16 312c0-13.3 10.7-24 24-24l7.6 0 .7 0L168 288c9.7 0 18.5 5.8 22.2 14.8s1.7 19.3-5.2 26.2l-41.1 41.1c62.6 61.5 163.1 61.2 225.3-1c17.5-17.5 30.1-38 37.8-59.8c5.9-16.7 24.2-25.4 40.8-19.5s25.4 24.2 19.5 40.8c-10.8 30.6-28.4 59.3-52.9 83.8c-87.2 87.2-228.3 87.5-315.8 1L57 457c-6.9 6.9-17.2 8.9-26.2 5.2S16 449.7 16 440l0-119.6 0-.7 0-7.6z" />
                                </svg>}
                        </button>
                    </OverlayTrigger>
                </td> : <td />}
            </tr>
        )
    }

    async function updatePin(dashboardId, unpin) {
        toast(`${unpin ? 'Unpinning' : 'Pinning'} your dashboard`, { type: toast.TYPE.INFO, autoClose: 5000 });

        try {
            setPinning(dashboardId);
            await API.put('DashboardApi', `/dashboard/${dashboardId}/pin`, {
                body: {
                    "unpin": unpin
                },
            });

            getAllDashboards();
            toast(`Dashboard ${unpin ? 'unpinned' : 'pinned'} successfully`, { type: toast.TYPE.SUCCESS, autoClose: 5000 });
        } catch (error) {
            toast(`There was a problem ${unpin ? 'unpinning' : 'pinning'} your dashboard`, { type: toast.TYPE.ERROR, autoClose: 5000 });
            setPinning(null);
        }
    }

    async function renewDashboard(dashboardId) {
        toast(`Renewing your dashboard`, { type: toast.TYPE.INFO, autoClose: 5000 });
        try {
            setRenewing(dashboardId);
            const today = new Date();
            const newExpiration = new Date(today.setFullYear(today.getFullYear() + 1));

            await API.put('DashboardApi', '/dashboard', {
                body: {
                    "dashboardId": dashboardId,
                    "expiration": formatDate(newExpiration)
                },
            });
            getAllDashboards(dashboardId);
            toast(`Dashboard renewed successfully`, { type: toast.TYPE.SUCCESS, autoClose: 5000 });
        } catch (error) {
            toast(`There was a problem renewing your dashboard`, { type: toast.TYPE.ERROR, autoClose: 5000 });
            setRenewing(null);
        }
    }

    async function getDashboard(dashboard) {
        toast(`Retrieving share data for your dashboard`, { type: toast.TYPE.INFO, autoClose: 5000 });
        setWaitingForDashboard(dashboard.id);
        try {
            const dashboardData = await API.get('DashboardApi', `/dashboard/${dashboard.id}`);

            const users = [];
            const accounts = [];
            const brands = [];
            dashboardData?.dashboardPermissionsShared?.map(data => {
                if (data.profile) {
                    users.push(data.profile);
                }
                if (data.account) {
                    const matchingAccount = availableAccounts.find(availableAccount =>
                        data.account.uuid === availableAccount.id
                    );
                    if (matchingAccount) {
                        data.account['brand'] = matchingAccount.brand;
                    }
                    accounts.push(data.account);
                }
                if (data.brand) {
                    brands.push(data.brand);
                }
            })

            setSharedUsers([...sortAlphabetical(users || [], 'email')]);
            setSharedAccounts([...sortAlphabetical(accounts || [], 'name')]);
            setSharedBrands([...sortAlphabetical(brands || [], 'name')]);

            setWaitingForDashboard(null);
            setSelectedDashboard(dashboard)
            setShowShareModal(true);
        } catch (error) {
            console.error(error);
            toast(`There was a problem getting your dashboard`, { type: toast.TYPE.ERROR, autoClose: 5000 });
            setShowShareModal(false);
            setWaitingForDashboard(null);
        }
    }

    return (<>
        <Route exact path="/dashboards">
            <Redirect to="/dashboards/owned" />
        </Route>
        <div className="container-fluid">
            <div className="card">
                <div className="card-header text-light font-weight-bold container-fluid" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <ul className="nav nav-tabs card-header-tabs">
                        <li
                            className="nav-item"
                            style={{ cursor: 'pointer' }}
                        >
                            <div className={activeTab === 'owned' ? 'nav-link active' : 'nav-link'}>
                                <Link to="/dashboards/owned" style={{ color: activeTab === 'owned' ? '#000' : '#fff' }} onClick={() => setActiveTab('owned')}>My Dashboards</Link>
                            </div>
                        </li>
                        <li
                            className="nav-item"
                            style={{ cursor: 'pointer' }}
                        >
                            <div className={activeTab === 'shared' ? 'nav-link active' : 'nav-link'}>
                                <OverlayTrigger
                                    key="sharedDashboard"
                                    placement="bottom"
                                    overlay={<Tooltip>Dashboards shared with you</Tooltip>}
                                >
                                    <label className="col-form-label p-0">
                                        <Link to="/dashboards/shared" style={{ color: activeTab === 'shared' ? '#000' : '#fff' }} onClick={() => setActiveTab('shared')}>Shared Dashboards</Link>
                                    </label>
                                </OverlayTrigger>
                            </div>
                        </li>
                    </ul>
                    {activeTab === 'owned' && <button type="button" className="account-header-button" style={{
                        backgroundColor: 'white',
                        borderRadius: '0.5rem',
                        border: 'none',
                        padding: '0.15rem 0.625rem 0.15rem 0.625rem',
                        fontWeight: 'normal'
                    }} onClick={() => { setShowAddModal(true) }} >
                        Add Dashboard
                    </button>}
                </div>
                <div className="card-body p-0">
                    <div className="row m-0">
                        <div className="col"></div>
                        <div className="col-auto mt-3">
                            {dashboards && (
                                <Switch>
                                    <Route exact path='/dashboards/owned'>
                                        <PhGridFilter
                                            filterString={filterString}
                                            setFilterString={setFilterString}
                                            setPaginationOffset={setOwnedPaginationOffset}
                                        />
                                    </Route>
                                    <Route exact path='/dashboards/shared'>
                                        <PhGridFilter
                                            filterString={filterString}
                                            setFilterString={setFilterString}
                                            setPaginationOffset={setSharedPaginationOffset}
                                        />
                                    </Route>
                                    <Route path='/dashboards'>
                                        <Redirect to='/dashboards/owned' />
                                    </Route>
                                </Switch>
                            )}
                        </div>
                    </div>
                    <table className="table table-striped table-responsive" style={{ padding: 0 }}>
                        <thead>
                            <tr>
                                <th scope="col"></th>
                                <th scope="col">
                                    <OverlayTrigger
                                        key="dashboardTitle"
                                        placement="top"
                                        overlay={<Tooltip>The name of the dashboard</Tooltip>}
                                    >
                                        <label className="col-form-label p-0">
                                            <PhGridSort
                                                columnLabel='Title'
                                                columnName={d => d?.name}
                                                config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }}
                                            />
                                        </label>
                                    </OverlayTrigger>
                                </th>
                                <th scope="col">
                                    <OverlayTrigger
                                        key="dashboardDescription"
                                        placement="top"
                                        overlay={<Tooltip>The description of the dashboard</Tooltip>}
                                    >
                                        <label className="col-form-label p-0">
                                            <PhGridSort
                                                columnLabel='Description'
                                                columnName={d => d?.description}
                                                config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }}
                                            />
                                        </label>
                                    </OverlayTrigger>
                                </th>
                                {activeTab === 'owned' && <th scope="col">
                                    <OverlayTrigger
                                        key="dashboardLifetime"
                                        placement="top"
                                        overlay={<Tooltip>The amount of days that a dashboard has until expiration</Tooltip>}
                                    >
                                        <label className="col-form-label p-0">
                                            <PhGridSort
                                                columnLabel='Dashboard Lifetime'
                                                columnName={d => d?.expiration}
                                                config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }}
                                            />
                                        </label>
                                    </OverlayTrigger>
                                </th>}
                                <th scope="col"></th>
                                <th scope="col"></th>
                                <th scope="col"></th>
                                <th scope="col"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {!dashboards && <tr><td colSpan="6" className="text-center">{waitingForDashboards && <InlineSpinner />}</td></tr>}
                            <Switch>
                                <Route exact path='/dashboards/owned'>
                                    {ownedDisplayList && ownedDisplayList.map(dashboard => (
                                        <React.Fragment key={dashboard.id}>
                                            {displayDashboards(dashboard)}
                                        </React.Fragment>
                                    ))}
                                </Route>
                                <Route exact path='/dashboards/shared'>
                                    {sharedDisplayList && sharedDisplayList.map(dashboard => (
                                        <React.Fragment key={dashboard.id}>
                                            {displayDashboards(dashboard)}
                                        </React.Fragment>
                                    ))}
                                </Route>
                                <Route path='/dashboards'>
                                    <Redirect to='/dashboards/owned' />
                                </Route>
                            </Switch>
                        </tbody>
                    </table>
                    {dashboards && (
                        <div className="row m-0 justify-content-end" style={{ paddingRight: '.75rem' }}>
                            <Switch>
                                <Route exact path='/dashboards/owned'>
                                    <PhGridPagination
                                        fullList={dashboards.filter((dashboard) => { return dashboard.ownership === 'owned' })}
                                        paginationOffset={ownedPaginationOffset}
                                        setPaginationOffset={setOwnedPaginationOffset}
                                        setPaginationSize={setOwnedPaginationSize}
                                        paginationSize={ownedPaginationSize}
                                        filterString={filterString}
                                    />
                                </Route>
                                <Route exact path='/dashboards/shared'>
                                    <PhGridPagination
                                        fullList={dashboards.filter((dashboard) => { return dashboard.ownership === 'shared' })}
                                        paginationOffset={sharedPaginationOffset}
                                        setPaginationOffset={setSharedPaginationOffset}
                                        setPaginationSize={setSharedPaginationSize}
                                        paginationSize={sharedPaginationSize}
                                        filterString={filterString}
                                    />
                                </Route>
                                <Route path='/dashboards'>
                                    <Redirect to='/dashboards/owned' />
                                </Route>
                            </Switch>
                        </div>
                    )}
                </div>
            </div>
            <Modal
                size="xl"
                show={showShareModal}
                onHide={() => { setShowShareModal(false) }}
            >
                <Modal.Header className="card-header">
                    <Modal.Title style={{ color: "white", marginLeft: "auto" }}>Share Dashboard</Modal.Title>
                    <button
                        type="button"
                        className="close"
                        onClick={() => setShowShareModal(false)}
                        style={{ color: "white" }}
                    >
                        <span aria-hidden="true" className="modal_button">&times;</span>
                        <span className="sr-only">Close</span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <DashboardSharing
                        dashboard={selectedDashboard}
                        setShowShareModal={setShowShareModal}
                        availableBrands={availableBrands}
                        availableAccounts={availableAccounts}
                        previouslySharedUsers={sharedUsers}
                        previouslySharedAccounts={sharedAccounts}
                        previouslySharedBrands={sharedBrands}
                    />
                </Modal.Body>
            </Modal>
            <Modal
                size="lg"
                show={showEditModal}
                onHide={() => { setShowEditModal(false) }}
            >
                <Modal.Header className="card-header">
                    <Modal.Title style={{ color: "white", marginLeft: "auto" }}>Edit Dashboard</Modal.Title>
                    <button
                        type="button"
                        className="close"
                        onClick={() => setShowEditModal(false)}
                        style={{ color: "white" }}
                    >
                        <span aria-hidden="true" className="modal_button">&times;</span>
                        <span className="sr-only">Close</span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <OwnerEdit
                        dashboard={selectedDashboard}
                        getAllDashboards={getAllDashboards}
                        setShowEditModal={setShowEditModal}
                    />
                </Modal.Body>
            </Modal>
            <Modal
                size="lg"
                show={showAddModal}
                onHide={() => { setShowAddModal(false) }}
            >
                <Modal.Header className="card-header">
                    <Modal.Title style={{ color: "white", marginLeft: "auto" }}>Add Dashboard</Modal.Title>
                    <button
                        type="button"
                        className="close"
                        onClick={() => setShowAddModal(false)}
                        style={{ color: "white" }}
                    >
                        <span aria-hidden="true" className="modal_button">&times;</span>
                        <span className="sr-only">Close</span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <AddDashboard
                        setShowAddModal={setShowAddModal}
                        getAllDashboards={getAllDashboards}
                        userPermission={dashboardUser?.permissions}
                    />
                </Modal.Body>
            </Modal>
        </div>
    </>);
};

function renderAvailableBrandCategories(
    availableAccountBrands,
    previouslySharedBrandNames,
    newlyAddedBrands,
    removedBrands,
    setNewlyAddedBrands,
    setRemovedBrands,
    setPreviouslySharedBrandNames,
    previouslySharedAccountIds,
    newlyAddedAccounts,
    removedAccounts,
    setNewlyAddedAccounts,
    setRemovedAccounts,
    setPreviouslySharedAccountIds
) {
    return availableAccountBrands.map(brand => {
        if (brand.accounts?.length > 0) {
            const brandChecked = (findArrayIndex(previouslySharedBrandNames, brand.name) > -1) || (findArrayIndex(newlyAddedBrands, brand.name) > -1);
            return (
                <div key={brand.name}>
                    <details>
                        <summary>
                            <input
                                type="checkbox"
                                name={brand.name}
                                value={brand.name}
                                onChange={e => handleOnChange(
                                    e,
                                    previouslySharedBrandNames,
                                    newlyAddedBrands,
                                    removedBrands,
                                    setNewlyAddedBrands,
                                    setRemovedBrands,
                                    setPreviouslySharedBrandNames
                                )}
                                checked={brandChecked}
                            />
                            {brand.name}
                        </summary>
                        <p>{brandChecked
                            ? renderDisabledAccountsCheckboxList(brand)
                            : renderAvailableAccountsCheckboxList(
                                brand,
                                previouslySharedAccountIds,
                                newlyAddedAccounts,
                                removedAccounts,
                                setNewlyAddedAccounts,
                                setRemovedAccounts,
                                setPreviouslySharedAccountIds
                            )
                        }</p>
                    </details>
                </div>
            )
        }
    })
}

function renderDisabledAccountsCheckboxList(brand) {
    return brand.accounts?.map(account => {
        return (
            <span key={account.id} style={{ marginLeft: 30 }}>
                <label>
                    <input
                        type="checkbox"
                        name={account.name}
                        value={account.id}
                        checked
                        disabled
                    />
                    {account.name}
                </label>
                <br />
            </span>
        )
    })
}

function renderAvailableAccountsCheckboxList(
    brand,
    previouslySharedAccountIds,
    newlyAddedAccounts,
    removedAccounts,
    setNewlyAddedAccounts,
    setRemovedAccounts,
    setPreviouslySharedAccountIds
) {
    return brand.accounts?.map(account => {
        return (
            <span key={account.id} style={{ marginLeft: 30 }}>
                <label>
                    <input
                        type="checkbox"
                        name={account.name}
                        value={account.id}
                        onChange={e => handleOnChange(
                            e,
                            previouslySharedAccountIds,
                            newlyAddedAccounts,
                            removedAccounts,
                            setNewlyAddedAccounts,
                            setRemovedAccounts,
                            setPreviouslySharedAccountIds
                        )}
                        checked={(findArrayIndex(previouslySharedAccountIds, account.id) > -1) || (findArrayIndex(newlyAddedAccounts, account.id) > -1)}
                    />
                    {account.name}
                </label>
                <br />
            </span>
        )
    })
}

function renderAvailableAccountCategories(
    availableUserAccounts,
    previouslySharedAccountIds,
    newlyAddedAccounts,
    removedAccounts,
    setNewlyAddedAccounts,
    setRemovedAccounts,
    setPreviouslySharedAccountIds,
    previouslySharedBrandNames,
    newlyAddedBrands,
    previouslySharedUserIds,
    newlyAddedUsers,
    removedUsers,
    setNewlyAddedUsers,
    setRemovedUsers,
    setPreviouslySharedUserIds
) {
    return availableUserAccounts.map(account => {
        if (account.users?.length > 0) {
            const accountId = account.uuid || account.id;
            const accountChecked = (findArrayIndex(previouslySharedAccountIds, accountId) > -1) || (findArrayIndex(newlyAddedAccounts, accountId) > -1);
            const brandChecked = (findArrayIndex(previouslySharedBrandNames, account.brand) > -1) || (findArrayIndex(newlyAddedBrands, account.brand) > -1);
            if (!brandChecked) {
                return (
                    <div key={accountId}>
                        <details>
                            <summary>
                                <input
                                    type="checkbox"
                                    name={account.name}
                                    value={accountId}
                                    onChange={e => handleOnChange(
                                        e,
                                        previouslySharedAccountIds,
                                        newlyAddedAccounts,
                                        removedAccounts,
                                        setNewlyAddedAccounts,
                                        setRemovedAccounts,
                                        setPreviouslySharedAccountIds
                                    )}
                                    checked={accountChecked}
                                />
                                {account.name}
                            </summary>
                            <p>{accountChecked
                                ? renderDisabledUsersCheckboxList(account)
                                : renderAvailableUsersCheckboxList(
                                    account,
                                    previouslySharedUserIds,
                                    newlyAddedUsers,
                                    removedUsers,
                                    setNewlyAddedUsers,
                                    setRemovedUsers,
                                    setPreviouslySharedUserIds
                                )}
                            </p>
                        </details>
                    </div>
                )
            } else {
                return (
                    <div key={accountId}>
                        <details>
                            <summary>
                                <input
                                    type="checkbox"
                                    name={account.name}
                                    value={accountId}
                                    checked
                                    disabled
                                />
                                {account.name}
                            </summary>
                            <p>{renderDisabledUsersCheckboxList(account)}</p>
                        </details>
                    </div>
                )
            }
        }
    })
}

function renderDisabledUsersCheckboxList(account) {
    return account.users?.map(user => {
        return (
            <span key={user.id} style={{ marginLeft: 30 }}>
                <label>
                    <input
                        type="checkbox"
                        name={user.email}
                        value={user.id}
                        checked
                        disabled
                    />
                    {user.email}
                </label>
                <br />
            </span>
        )
    })
}

function renderAvailableUsersCheckboxList(
    account,
    previouslySharedUserIds,
    newlyAddedUsers,
    removedUsers,
    setNewlyAddedUsers,
    setRemovedUsers,
    setPreviouslySharedUserIds
) {
    return account.users?.map(user => {
        return (
            <span key={user.id} style={{ marginLeft: 30 }}>
                <label>
                    <input
                        type="checkbox"
                        name={user.email}
                        value={user.id}
                        onChange={e => handleOnChange(
                            e,
                            previouslySharedUserIds,
                            newlyAddedUsers,
                            removedUsers,
                            setNewlyAddedUsers,
                            setRemovedUsers,
                            setPreviouslySharedUserIds
                        )}
                        checked={(findArrayIndex(previouslySharedUserIds, user.id) > -1) || (findArrayIndex(newlyAddedUsers, user.id) > -1)}
                    />
                    {user.email}
                </label>
                <br />
            </span>
        )
    })
}

function renderSharedBrandsCheckboxList(
    previouslySharedBrands,
    previouslySharedBrandNames,
    newlyAddedBrands,
    removedBrands,
    setNewlyAddedBrands,
    setRemovedBrands,
    setPreviouslySharedBrandNames
) {
    return previouslySharedBrands.map(brand => (
        <span key={brand.name}>
            <label>
                <input
                    type="checkbox"
                    name={brand.name}
                    value={brand.name}
                    onChange={e => handleOnChange(
                        e,
                        previouslySharedBrandNames,
                        newlyAddedBrands,
                        removedBrands,
                        setNewlyAddedBrands,
                        setRemovedBrands,
                        setPreviouslySharedBrandNames
                    )}
                    checked={(findArrayIndex(previouslySharedBrandNames, brand.name) > -1) || (findArrayIndex(newlyAddedBrands, brand.name) > -1)}
                />
                {brand.name}
            </label>
            <br />
        </span>
    ))
}

function renderSharedAccountsCheckboxList(
    previouslySharedAccounts,
    previouslySharedAccountIds,
    newlyAddedAccounts,
    removedAccounts,
    setNewlyAddedAccounts,
    setRemovedAccounts,
    setPreviouslySharedAccountIds
) {
    return previouslySharedAccounts.map(account => (
        <span key={account.uuid}>
            <label>
                <input
                    type="checkbox"
                    name={account.name}
                    value={account.uuid}
                    onChange={e => handleOnChange(
                        e,
                        previouslySharedAccountIds,
                        newlyAddedAccounts,
                        removedAccounts,
                        setNewlyAddedAccounts,
                        setRemovedAccounts,
                        setPreviouslySharedAccountIds
                    )}
                    checked={(findArrayIndex(previouslySharedAccountIds, account.uuid) > -1) || (findArrayIndex(newlyAddedAccounts, account.uuid) > -1)}
                />
                {account.name} ({account.brand})
            </label>
            <br />
        </span>
    ))
}

function renderSharedUsersCheckboxList(
    previouslySharedUsers,
    previouslySharedUserIds,
    newlyAddedUsers,
    removedUsers,
    setNewlyAddedUsers,
    setRemovedUsers,
    setPreviouslySharedUserIds
) {
    return previouslySharedUsers.map(user => (
        <span key={user.uuid}>
            <label>
                <input
                    type="checkbox"
                    name={user.email}
                    value={user.uuid}
                    onChange={e => handleOnChange(
                        e,
                        previouslySharedUserIds,
                        newlyAddedUsers,
                        removedUsers,
                        setNewlyAddedUsers,
                        setRemovedUsers,
                        setPreviouslySharedUserIds
                    )}
                    checked={(findArrayIndex(previouslySharedUserIds, user.uuid) > -1) || (findArrayIndex(newlyAddedUsers, user.uuid) > -1)}
                />
                {user.email} ({user.accounts[0].name})
            </label>
            <br />
        </span>
    ))
}

const handleOnChange = (
    e,
    previouslySharedSet,
    newlyAddedSet,
    removedSet,
    setNewlyAddedSet,
    setRemovedSet,
    setPreviouslySharedSet
) => {
    const newItem = e.target.value;
    const newlyAddedSetCopy = [...newlyAddedSet];
    const removedSetCopy = [...removedSet];
    const previouslySharedSetCopy = [...previouslySharedSet];
    const addedIndex = findArrayIndex(newlyAddedSetCopy, newItem);
    const existingIndex = findArrayIndex(previouslySharedSetCopy, newItem);

    if (addedIndex > -1) {
        // if user/account/brand has already been selected to be shared, remove it
        newlyAddedSetCopy.splice(addedIndex, 1);
        setNewlyAddedSet([...newlyAddedSetCopy]);
    } else if (existingIndex > -1) {
        // only add to removed user/account/brand if it was already shared with them
        removedSetCopy.push(newItem);
        setRemovedSet([...removedSetCopy]);
        previouslySharedSetCopy.splice(existingIndex, 1);
        setPreviouslySharedSet([...previouslySharedSetCopy]);
    } else {
        // if user/account/brand has not been selected to be shared until now, add it
        newlyAddedSetCopy.push(newItem);
        setNewlyAddedSet([...newlyAddedSetCopy]);
    }
};

function DashboardSharing({
    dashboard,
    setShowShareModal,
    availableBrands,
    availableAccounts,
    previouslySharedUsers,
    previouslySharedAccounts,
    previouslySharedBrands
}) {
    const [availableAccountBrands, setAvailableAccountBrands] = useState([]);
    const [availableUserAccounts, setAvailableUserAccounts] = useState([]);
    const [waitingForShare, setWaitingForShare] = useState(false);
    const [removedUsers, setRemovedUsers] = useState([]);
    const [removedAccounts, setRemovedAccounts] = useState([]);
    const [removedBrands, setRemovedBrands] = useState([]);
    const [newlyAddedUsers, setNewlyAddedUsers] = useState([]);
    const [newlyAddedAccounts, setNewlyAddedAccounts] = useState([]);
    const [newlyAddedBrands, setNewlyAddedBrands] = useState([]);
    const [previouslySharedUserIds, setPreviouslySharedUserIds] = useState([]);
    const [previouslySharedAccountIds, setPreviouslySharedAccountIds] = useState([]);
    const [previouslySharedBrandNames, setPreviouslySharedBrandNames] = useState([]);

    useEffect(() => {
        if (availableBrands) {
            setAvailableAccountBrands([...availableBrands]);
        }
        if (availableAccounts) {
            setAvailableUserAccounts([...availableAccounts]);
        }
        if (previouslySharedUsers.length > 0) {
            setPreviouslySharedUserIds(previouslySharedUsers.map(user => { return user.uuid }));
        }
        if (previouslySharedAccounts.length > 0) {
            setPreviouslySharedAccountIds(previouslySharedAccounts.map(account => { return account.uuid }));
        }
        if (previouslySharedBrands.length > 0) {
            setPreviouslySharedBrandNames(previouslySharedBrands.map(brand => { return brand.name }));
        }
        setInitialShared();
    }, [availableBrands, availableAccounts, previouslySharedUsers, previouslySharedAccounts, previouslySharedBrands]);

    function setInitialShared() { 
        setNewlyAddedAccounts([]);
        setNewlyAddedBrands([]);
        setNewlyAddedUsers([]);
        setRemovedAccounts([]);
        setRemovedBrands([]);
        setRemovedUsers([]);
    }

    async function shareDashboard() {
        setWaitingForShare(true);
        try {
            if (newlyAddedUsers.length > 0 || newlyAddedAccounts.length > 0 || newlyAddedBrands.length > 0) {
                await API.put('DashboardApi', `/dashboard/${dashboard.id}/share`, {
                    body: {
                        "dashboardId": dashboard.id,
                        "userIds": newlyAddedUsers,
                        "accountIds": newlyAddedAccounts,
                        "brandNames": newlyAddedBrands,
                        "revoke": false
                    },
                });
            }
            if (removedUsers.length > 0 || removedAccounts.length > 0 || removedBrands.length > 0) {
                await API.put('DashboardApi', `/dashboard/${dashboard.id}/share`, {
                    body: {
                        "dashboardId": dashboard.id,
                        "userIds": removedUsers,
                        "accountIds": removedAccounts,
                        "brandNames": removedBrands,
                        "revoke": true
                    },
                });
            }
            setWaitingForShare(false);
            setShowShareModal(false);
            toast(`Dashboard shared successfully`, { type: toast.TYPE.SUCCESS, autoClose: 5000 });
        } catch (error) {
            toast(`There was a problem sharing your dashboard`, { type: toast.TYPE.ERROR, autoClose: 5000 });
            setWaitingForShare(false);
            setShowShareModal(false);
        }
    }

    return (
        <div>
            <div className="container">
                <div className="row">
                    <div className="col-md border p-2 ml-2" style={{ maxHeight: '25rem', overflow: 'auto' }}>
                        <b>Available Accounts by Brand:</b><br />
                        <span key={availableBrands}>
                            {(availableAccountBrands.length > 0 && availableAccountBrands[0].accounts)
                                ? renderAvailableBrandCategories(
                                    availableAccountBrands,
                                    previouslySharedBrandNames,
                                    newlyAddedBrands,
                                    removedBrands,
                                    setNewlyAddedBrands,
                                    setRemovedBrands,
                                    setPreviouslySharedBrandNames,
                                    previouslySharedAccountIds,
                                    newlyAddedAccounts,
                                    removedAccounts,
                                    setNewlyAddedAccounts,
                                    setRemovedAccounts,
                                    setPreviouslySharedAccountIds
                                ) : availableAccountBrands[0] === 'none'
                                    ? 'You do not have permission to share to brands'
                                    : <InlineSpinner />
                            }
                        </span>
                    </div>
                    <div className="col-md border p-2 ml-2" style={{ maxHeight: '25rem', overflow: 'auto' }}>
                        <b>Available Users by Account:</b><br />
                        <span key={availableAccounts}>
                            {(availableUserAccounts.length > 0 && availableUserAccounts[0].users)
                                ? renderAvailableAccountCategories(
                                    availableUserAccounts,
                                    previouslySharedAccountIds,
                                    newlyAddedAccounts,
                                    removedAccounts,
                                    setNewlyAddedAccounts,
                                    setRemovedAccounts,
                                    setPreviouslySharedAccountIds,
                                    previouslySharedBrandNames,
                                    newlyAddedBrands,
                                    previouslySharedUserIds,
                                    newlyAddedUsers,
                                    removedUsers,
                                    setNewlyAddedUsers,
                                    setRemovedUsers,
                                    setPreviouslySharedUserIds
                                ) : availableUserAccounts[0] === 'none'
                                    ? 'You do not have permission to share to accounts'
                                    : <InlineSpinner />
                            }
                        </span>
                    </div>
                    <div className="col-md border p-2 ml-2" style={{ maxHeight: '25rem', overflow: 'auto' }}>
                        <b>Current Dashboard Viewers:</b><br />
                        <details open>
                            <summary><u>Brands</u></summary>
                            <p>{previouslySharedBrands.length > 0
                                ? renderSharedBrandsCheckboxList(
                                    previouslySharedBrands,
                                    previouslySharedBrandNames,
                                    newlyAddedBrands,
                                    removedBrands,
                                    setNewlyAddedBrands,
                                    setRemovedBrands,
                                    setPreviouslySharedBrandNames
                                ) : 'Dashboard not currently shared with any brands'}
                            </p>
                        </details>
                        <details open>
                            <summary><u>Accounts</u></summary>
                            <p>{previouslySharedAccounts.length > 0
                                ? renderSharedAccountsCheckboxList(
                                    previouslySharedAccounts,
                                    previouslySharedAccountIds,
                                    newlyAddedAccounts,
                                    removedAccounts,
                                    setNewlyAddedAccounts,
                                    setRemovedAccounts,
                                    setPreviouslySharedAccountIds
                                ) : 'Dashboard not currently shared with any accounts'}
                            </p>
                        </details>
                        <details open>
                            <summary><u>Users</u></summary>
                            <p>{previouslySharedUsers.length > 0
                                ? renderSharedUsersCheckboxList(
                                    previouslySharedUsers,
                                    previouslySharedUserIds,
                                    newlyAddedUsers,
                                    removedUsers,
                                    setNewlyAddedUsers,
                                    setRemovedUsers,
                                    setPreviouslySharedUserIds
                                ) : 'Dashboard not currently shared with any users'}
                            </p>
                        </details>
                    </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', padding: '0.75rem 0.75rem 0rem 0.75rem', marginTop: '5px' }}>
                    <Button variant="danger" className="btn mr-2" style={{ color: 'white', border: 'none', borderRadius: '0.5rem' }} onClick={() => setInitialShared()}>Reset</Button>
                    <Button variant="primary" className="btn mr-2" style={{ color: 'white', border: 'none', borderRadius: '0.5rem' }} onClick={() => shareDashboard()}>
                        Save {waitingForShare && <InlineSpinner />}
                    </Button>
                    <Button variant="secondary" className="btn mr-2" style={{ color: 'white', border: 'none', borderRadius: '0.5rem' }} onClick={() => setShowShareModal(false)}>Cancel</Button>
                </div>
            </div>
        </div>
    )
}

function validateDescription(dashboardDescription) {
    const rg1 = /^[0-9A-Za-z_.-\s]+$/; // alphanumeric, hyphens, underscores, periods and spaces
    return rg1.test(dashboardDescription) && dashboardDescription.length < 256;
}

function validateEditing(id, description, updateFrequency, expiration) {
    if (id === '' || description === '' || updateFrequency === '' || expiration === '' || !validateDescription(description)) {
        return false;
    }
    return true;
}

function OwnerEdit({
    dashboard,
    getAllDashboards,
    setShowEditModal
}) {
    const [dashboardDescription, setDashboardDescription] = useState('');
    const [dashboardUpdateFrequency, setDashboardUpdateFrequency] = useState('')
    const [dashboardExpiration, setDashboardExpiration] = useState('')
    const [waitingForResponse, setWaitingForResponse] = useState(false);

    const today = new Date();
    const originalExpiration = new Date(dashboard.expiration).valueOf();

    useEffect(() => {
        if (dashboard) {
            if (dashboard.description) {
                setDashboardDescription(dashboard.description);
            }
            if (dashboard.updateFrequency) {
                setDashboardUpdateFrequency(dashboard.updateFrequency.toLowerCase());
            }
            if (dashboard.expiration) {
                setDashboardExpiration(new Date(dashboard.expiration));
            }
        }
    }, [dashboard]);

    async function updateDashboard() {
        setWaitingForResponse(true);
        try {
            await API.put('DashboardApi', '/dashboard', {
                body: {
                    "dashboardId": dashboard.id,
                    "expiration": formatDate(dashboardExpiration),
                    "updateFrequency": dashboardUpdateFrequency,
                    "description": dashboardDescription
                },
            });
            getAllDashboards();
            setShowEditModal(false);
            setWaitingForResponse(false);
            toast(`Dashboard updated successfully`, { type: toast.TYPE.SUCCESS, autoClose: 5000 });
        } catch (error) {
            toast(`There was a problem updating your dashboard`, { type: toast.TYPE.ERROR, autoClose: 5000 });
            setShowEditModal(false);
            setWaitingForResponse(false);
        }
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="form-group row">
                        <OverlayTrigger
                            key="dashboardTitle"
                            placement="left"
                            overlay={<Tooltip>Name of the Dashboard</Tooltip>}
                        >
                            <label className="col-2 col-form-label">Title:</label>
                        </OverlayTrigger>
                        <input
                            type="text"
                            className="form-control col-9"
                            value={dashboard.name}
                            disabled
                            title="Title can only be edited in QuickSight"
                        />
                    </div>
                    <div className="form-group row">
                        <OverlayTrigger
                            key="dashboardDescription"
                            placement="left"
                            overlay={<Tooltip>Provide a description for the dashboard</Tooltip>}
                        >
                            <label className="col-2 col-form-label">Description:</label>
                        </OverlayTrigger>
                        <textarea
                            className={`form-control col-9 ${dashboardDescription && !validateDescription(dashboardDescription) ? 'is-invalid' : ''}`}
                            value={dashboardDescription}
                            onChange={(e) => setDashboardDescription(e.target.value)}
                            placeholder="Dashboard Description"
                        />
                        {(dashboardDescription && !validateDescription(dashboardDescription)) && <div className="offset-2 invalid-feedback">
                            Must be under 256 characters long and may only include alphanumeric characters, hyphens, underscores, periods and spaces.
                        </div>}
                    </div>
                    {!isDashboardQueryVis(dashboard.id) && <div className="form-group row">
                        <OverlayTrigger
                            key="dashboardExpiration"
                            placement="left"
                            overlay={<Tooltip>Select the expiration of the dashboard</Tooltip>}
                        >
                            <label className="col-2 col-form-label">Dashboard Expiration:</label>
                        </OverlayTrigger>
                        <div className="col-sm-3">
                            <DatePicker
                                name="dashboardExpiration"
                                showYearDropdown
                                selected={dashboardExpiration}
                                maxDate={new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())}
                                onChange={date => setDashboardExpiration(date)}
                                className='form-control col-9'
                                placeholderText="Set Expiration Date"
                            />
                        </div>
                        {(new Date(dashboardExpiration).valueOf() < originalExpiration) &&
                            <div className="offset-2 invalid-feedback text-orange" style={{ display: 'block' }}>
                                The selected expiration date is earlier than the previously set expiration. Verify selection before submitting.
                            </div>
                        }
                    </div>}
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'end' }}>
                <Button variant="primary" className="btn mr-2" style={{ color: 'white', border: 'none', borderRadius: '0.5rem' }} disabled={!validateEditing(dashboard.id, dashboardDescription, dashboardUpdateFrequency, dashboardExpiration)} onClick={() => updateDashboard()}>
                    Save {waitingForResponse && <InlineSpinner />}
                </Button>
                <Button variant="secondary" className="btn mr-2" style={{ color: 'white', border: 'none', borderRadius: '0.5rem' }} onClick={() => setShowEditModal(false)}>Cancel</Button>
            </div>
        </div>
    );
}

function AddDashboard({
    setShowAddModal,
    getAllDashboards,
    userPermission
}) {
    const today = new Date();
    const isAuthor = userPermission === 'author';
    const [dashboardId, setDashboardId] = useState('');
    const [isQueryVis, setIsQueryVis] = useState(!isAuthor);
    const [dashboardDescription, setDashboardDescription] = useState('');
    const dashboardUpdateFrequency = 'none';
    const [dashboardExpiration, setDashboardExpiration] = useState(new Date(today.getFullYear(), today.getMonth() + 3, today.getDate()));
    const [waitingForResponse, setWaitingForResponse] = useState(false);
    const [dashboardIdError, setDashboardIdError] = useState(null);

    function validateUUID(uuid) {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return uuidRegex.test(uuid);
    }

    async function addDashboard() {
        setWaitingForResponse(true);

        try {
            const idToUse = isQueryVis ? 'dash_query_vis_' + dashboardId : dashboardId;
            await API.put('DashboardApi', '/dashboard', {
                body: {
                    "dashboardId": idToUse,
                    "expiration": formatDate(dashboardExpiration),
                    "updateFrequency": dashboardUpdateFrequency,
                    "description": dashboardDescription
                },
            });
            getAllDashboards();
            setShowAddModal(false);
            setWaitingForResponse(false);
            toast(`Dashboard added successfully`, { type: toast.TYPE.SUCCESS, autoClose: 5000 });
        } catch (error) {
            if (error?.response?.data?.error === 'Dashboard not found.') {
                setDashboardIdError('Dashboard not found. Please verify your ID and Query Visualization checkbox and try again.');
            } else if (error?.response?.status === 403) {
                setDashboardIdError('You do not have permission to add this dashboard. Verify the ID or ask the owner to share the dashboard with you.');
            } else {
                toast(`There was a problem adding your dashboard`, { type: toast.TYPE.ERROR, autoClose: 5000 });
                setShowAddModal(false);
            }
            setWaitingForResponse(false);
        }
        }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="form-group row">
                        <OverlayTrigger
                            key="dashboardId"
                            placement="left"
                            overlay={<Tooltip>Dashboard ID from QuickSight or Job ID from Query My Files</Tooltip>}
                        >
                            <label className="col-2 col-form-label">ID:</label>
                        </OverlayTrigger>
                        <input
                            type="text"
                            className={`form-control col-9 ${dashboardId && (dashboardIdError || !validateUUID(dashboardId)) ? 'is-invalid' : ''}`}
                            value={dashboardId}
                            onChange={(e) => { setDashboardId(e.target.value); setDashboardIdError(null); }}
                        />
                        {(dashboardId && !validateUUID(dashboardId)) && <div className="offset-2 invalid-feedback">
                            Invalid ID. Please only enter valid dashboard or query visualization IDs.
                        </div>}
                        {(dashboardId && dashboardIdError) && <div className="offset-2 invalid-feedback">
                            {dashboardIdError}
                        </div>}
                    </div>
                    <div className="form-group row" style={{ alignItems: 'center' }}>
                        <OverlayTrigger
                            key="isQueryVis"
                            placement="left"
                            overlay={<Tooltip>Check this if your dashboard is a Query Visualization</Tooltip>}
                        >
                            <label className="col-2 col-form-label">Query Visualization:</label>
                        </OverlayTrigger>
                        <input
                            type="checkbox"
                            name="isQueryVis"
                            className="form-control"
                            style={{ width: '20px', height: '20px', alignContent: 'center' }}
                            onChange={(e) => { setIsQueryVis(e.target.checked); setDashboardIdError(null); }}
                            checked={isQueryVis}
                            disabled={!isAuthor}
                            title={!isAuthor ? 'Author permissions required to add QuickSight Dashboards' : ''}
                        />
                    </div>
                    <div className="form-group row">
                        <OverlayTrigger
                            key="dashboardDescription"
                            placement="left"
                            overlay={<Tooltip>Provide a description for the dashboard</Tooltip>}
                        >
                            <label className="col-2 col-form-label">Description:</label>
                        </OverlayTrigger>
                        <textarea
                            className={`form-control col-9 ${dashboardDescription && !validateDescription(dashboardDescription) ? 'is-invalid' : ''}`}
                            value={dashboardDescription}
                            onChange={(e) => setDashboardDescription(e.target.value)}
                            placeholder="Dashboard Description"
                        />
                        {(dashboardDescription && !validateDescription(dashboardDescription)) && <div className="offset-2 invalid-feedback">
                            Must be under 256 characters long and may only include alphanumeric characters, hyphens, underscores, periods and spaces.
                        </div>}
                    </div>
                    {!isQueryVis && <div className="form-group row">
                        <OverlayTrigger
                            key="dashboardExpiration"
                            placement="left"
                            overlay={<Tooltip>Select the expiration of the dashboard</Tooltip>}
                        >
                            <label className="col-2 col-form-label">Dashboard Expiration:</label>
                        </OverlayTrigger>
                        <div className="col-sm-3">
                            <DatePicker
                                name="dashboardExpiration"
                                showYearDropdown
                                selected={dashboardExpiration}
                                minDate={today}
                                maxDate={new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())}
                                onChange={date => setDashboardExpiration(date)}
                                className='form-control col-9'
                                placeholderText="Set Expiration Date"
                            />
                        </div>
                    </div>}
                </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', borderTop: '1px solid #dee2e6', padding: '0.75rem 0.75rem 0rem 0.75rem' }}>
                <Button variant="primary" className="btn mr-2" style={{ color: 'white', border: 'none', borderRadius: '0.5rem' }} disabled={(!validateEditing(dashboardId, dashboardDescription, dashboardUpdateFrequency, dashboardExpiration) || !validateUUID(dashboardId))} onClick={() => addDashboard()}>
                    Save {waitingForResponse && <InlineSpinner />}
                </Button>
                <Button variant="secondary" className="btn mr-2" style={{ color: 'white', border: 'none', borderRadius: '0.5rem' }} onClick={() => setShowAddModal(false)}>Cancel</Button>
            </div>
        </div>
    );
}

export default DashboardsContainer;
